import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Alert,
  Collapse,
} from '@mui/material'
import { Sports, TrendingUp, Person, Star, CheckCircle } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useStudentProfile } from '../hooks/useStudentProfile'
import { useOnboarding } from '../hooks/useOnboarding'
import { useRecruiterProfile } from '../hooks/useRecruiterProfile'
import { useRecruiterBookmarks } from '../hooks/useBookmarks'
import { TRACK_EVENTS, UserRole } from '@tnf-recruit/shared'
import { AppPageHeader } from '../components/AppPageHeader'
import { LoadingStatePanel } from '../components/StatePanel'
import { QuickActionsCard } from '../components/QuickActionsCard'
import { useAuth } from '../hooks/useAuth'
import { useUser } from '../hooks/useUser'

const getEventName = (id: string) => TRACK_EVENTS.find(e => e.id === id)?.name ?? id

const STUDENT_DASHBOARD_WELCOME_KEY = 'tnf.student.dashboard.welcome.dismissed'

export const DashboardPage = () => {
  const { user } = useAuth()
  const { data: dbUser } = useUser()

  const userRole = dbUser?.role
  const isStudent = userRole === UserRole.STUDENT
  const isRecruiter = userRole === UserRole.RECRUITER
  const { profile: studentProfile } = useStudentProfile({ enabled: isStudent })
  const { profile: recruiterProfile } = useRecruiterProfile({ enabled: isRecruiter })
  const authFirstName = user?.given_name ?? user?.name?.split(' ')[0]
  const profileFirstName = isStudent
    ? studentProfile.data?.personalInfo?.firstName
    : recruiterProfile.data?.contactInfo?.firstName
  const firstName = profileFirstName?.trim() || authFirstName || user?.email?.split('@')[0]

  return (
    <Box>
      <AppPageHeader
        title={`Welcome back, ${firstName}!`}
        description={
          isStudent
            ? 'Track your recruiting progress and manage your athletic profile.'
            : 'Monitor your recruiting activities and discover new talent.'
        }
      />

      {isStudent && <StudentDashboard />}
      {isRecruiter && <RecruiterDashboard />}
    </Box>
  )
}

// ─── Student Dashboard ──────────────────────────────────────────────────────

function StudentDashboard() {
  const navigate = useNavigate()
  const { profile } = useStudentProfile()
  const { profileComplete, onboardingStatusLoading } = useOnboarding()
  const [showProfileWelcome, setShowProfileWelcome] = useState(false)

  useEffect(() => {
    if (profile.isLoading || onboardingStatusLoading) return
    if (!profileComplete) {
      setShowProfileWelcome(false)
      return
    }
    try {
      if (localStorage.getItem(STUDENT_DASHBOARD_WELCOME_KEY) === '1') {
        setShowProfileWelcome(false)
      } else {
        setShowProfileWelcome(true)
      }
    } catch {
      setShowProfileWelcome(true)
    }
  }, [profile.isLoading, onboardingStatusLoading, profileComplete, profile.data])

  const dismissProfileWelcome = () => {
    try {
      localStorage.setItem(STUDENT_DASHBOARD_WELCOME_KEY, '1')
    } catch {
      // ignore
    }
    setShowProfileWelcome(false)
  }

  if (profile.isLoading || onboardingStatusLoading) {
    return (
      <LoadingStatePanel
        title="Loading dashboard"
        description="Fetching your latest profile data."
      />
    )
  }

  const completion = profile.data?.profileCompletion ?? 0

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Collapse in={showProfileWelcome}>
          <Alert
            severity="success"
            icon={<CheckCircle fontSize="inherit" />}
            onClose={dismissProfileWelcome}
            sx={{ alignItems: 'center', mb: 2 }}
          >
            <Typography variant="body2" component="span">
              <strong>Welcome to your home base.</strong> Your profile is complete. Keep your PRs
              and contact information current so coaches see your best work.
            </Typography>
          </Alert>
        </Collapse>
      </Grid>

      {/* Profile Completion */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Person sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Profile Completion</Typography>
            </Box>
            <LinearProgress variant="determinate" value={completion} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {completion}% complete
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                completion === 100 ? navigate('/profile') : navigate('/profile/edit')
              }
              sx={{ mt: 2 }}
            >
              {completion === 100 ? 'View Profile' : 'Complete Profile'}
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Events */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Sports sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Your Events</Typography>
            </Box>
            {(() => {
              const events: string[] = profile.data?.athleticInfo?.primaryEvents ?? []
              return events.length > 0 ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {events.map(id => (
                    <Chip key={id} label={getEventName(id)} />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No events selected. Update your profile to add events.
                </Typography>
              )
            })()}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <QuickActionsCard
          actions={[
            {
              label: 'Review Profile',
              variant: 'contained',
              icon: <Sports />,
              onClick: () => navigate('/profile'),
            },
            {
              label: 'Update PRs or Achievements',
              icon: <TrendingUp />,
              onClick: () => navigate('/profile/edit'),
            },
          ]}
        />
      </Grid>
    </Grid>
  )
}

// ─── Recruiter Dashboard ────────────────────────────────────────────────────

function RecruiterDashboard() {
  const navigate = useNavigate()
  const { dashboard } = useRecruiterProfile()
  const { bookmarks } = useRecruiterBookmarks()

  if (dashboard.isLoading) {
    return (
      <LoadingStatePanel
        title="Loading dashboard"
        description="Fetching your recruiter activity and saved athletes."
      />
    )
  }

  const stats = dashboard.data ?? { totalStudents: 0, savedProfiles: 0 }

  return (
    <Grid container spacing={3}>
      {/* Stats Cards */}
      {/* Total Students card temporarily hidden. The server still sends the count,
          so restoring this is just uncommenting this block.
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Sports sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Total Students</Typography>
            </Box>
            <Typography variant="h3" color="primary" gutterBottom>
              {stats.totalStudents.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Available athletes
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      */}

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Star sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Saved Profiles</Typography>
            </Box>
            <Typography variant="h3" color="primary" gutterBottom>
              {stats.savedProfiles}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Athletes you're tracking
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Saved Athletes */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Saved Athletes
            </Typography>
            {bookmarks.data && bookmarks.data.length > 0 ? (
              <List>
                {bookmarks.data.slice(0, 5).map((bk: any) => {
                  const sp = bk.studentProfiles ?? {}
                  const pi = sp.personalInfo ?? {}
                  return (
                    <ListItem
                      key={bk.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/student/${bk.studentId}`)}
                    >
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <Person />
                      </Avatar>
                      <ListItemText
                        primary={`${pi.firstName ?? ''} ${pi.lastName ?? ''}`}
                        secondary={sp.academicInfo?.highSchool ?? ''}
                      />
                    </ListItem>
                  )
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No saved athletes yet. Browse athletes and bookmark profiles to track prospects.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <QuickActionsCard
          actions={[
            {
              label: 'Browse Athletes',
              variant: 'contained',
              icon: <Sports />,
              onClick: () => navigate('/browse'),
            },
            { label: 'Manage Subscription', onClick: () => navigate('/subscription') },
          ]}
        />
      </Grid>
    </Grid>
  )
}
